// Read a drawn board back into a board.
//
// The gallery keeps its positions as the text `tools/lib/render.mjs` drew —
// which is what makes them cheap to record, and what makes them a *picture*
// rather than a board. This turns one back into a real `GameState`, so the
// dashboard can put the actual renderer on it.
//
// **Lossy on purpose, and the losses are known.** The drawing was made for a
// terminal, so several things it shows are not reversible:
//
// - A **loaded** belt draws as `•`, which says a lane is there and not which
//   way it points. Those come back pointing north.
// - A heap over nine draws as `*`. Those come back as ten.
// - A piece standing on a belt or a heap hides it entirely; the piece wins the
//   square, exactly as it does in the real renderer.
// - Nothing carried is drawn at all — held material is in the tally line, not
//   on the board.
// - Facing is not drawn, so every piece comes back on its side's own forward.
//
// None of that matters for the thing this is for, which is looking at the
// shape of a position. It is a reconstruction, not a replay.
//
// **It goes through `applyCommand`.** Everything here is board *setup* a
// `new-game` already describes — `cells`, `armies`, `heaps`, `belts` — so a
// parsed board is an opening command like a scenario's, and no state is built
// by hand.

import { applyCommand, type GameState, newGame } from "src/engine/rules/commands";
import type { Color, Direction, PieceType } from "src/engine/rules/board";

const SHIFT = 10;
const BIAS = 512;
const square = (file: number, rank: number): number =>
  ((rank + BIAS) << SHIFT) | (file + BIAS);

/** The letters `renderBoard` uses, in `PieceType` order. */
const TYPE_OF: Record<string, PieceType> = {
  P: 1 as PieceType,
  N: 2 as PieceType,
  B: 3 as PieceType,
  R: 4 as PieceType,
  Q: 5 as PieceType,
  K: 6 as PieceType
};

/** `^ > v <` is north, east, south, west — `Direction` order. */
const DIR_OF: Record<string, Direction> = {
  "^": 0 as Direction,
  ">": 1 as Direction,
  v: 2 as Direction,
  "<": 3 as Direction
};

/** A heap drawn as `*` is "more than nine" and comes back as this. */
const LOTS = 10;

/** Every row of the drawing, with the rank it carries. */
interface Row {
  rank: number;
  cells: string;
}

/**
 * The rows of a drawn board, and where its left edge is.
 *
 * A row is `String(rank).padStart(3) + " "` and then one character per file,
 * so the rank is exact and the file has to be worked out from the ruler.
 */
function rowsOf(lines: readonly string[]): Row[] {
  const rows: Row[] = [];
  for (const line of lines) {
    const label = line.slice(0, 3).trim();
    // The ruler and its labels have no rank, which is what tells them apart
    // from a row — every real row starts with a number.
    if (!/^-?\d+$/.test(label)) continue;
    rows.push({ rank: Number(label), cells: line.slice(4) });
  }
  return rows;
}

/**
 * Which file the drawing's first column is.
 *
 * The ruler marks every fifth file with `|` and labels it `|file| % 10`, which
 * does not identify the file on its own: a `|` under a `0` is file 0, or ±10,
 * or ±20. So the candidates are enumerated and the one putting the board
 * nearest the origin wins.
 *
 * **Wrong only ever shifts the picture sideways.** Every board the tuner plays
 * is the classic sixty-four or that padded four out, and both come out exact;
 * anything else is drawn a few files along from where it really sat, which is
 * a caption being wrong rather than a position being wrong.
 */
export function firstFile(ruler: string, labels: string): number {
  const at = ruler.indexOf("|", 4);
  if (at < 0) return 0;
  const column = at - 4;
  const digit = Number(labels[at]);
  if (!Number.isFinite(digit)) return -column;
  let best = 0;
  let bestDistance = Infinity;
  // Every multiple of five whose last digit matches, near the origin.
  for (let candidate = -60; candidate <= 60; candidate += 5) {
    if (Math.abs(candidate) % 10 !== digit) continue;
    const start = candidate - column;
    if (Math.abs(start) < bestDistance) {
      bestDistance = Math.abs(start);
      best = start;
    }
  }
  return best;
}

/** What a drawn board says is on it. */
export interface ParsedBoard {
  cells: number[];
  armies: [square: number, side: Color, type: PieceType, facing: Direction][];
  heaps: [number, number][];
  belts: [square: number, direction: Direction, owner: Color][];
}

/**
 * Read a drawn board.
 *
 * A blank is *not a cell* — that is how `renderBoard` draws a hole or the
 * outside of a ragged board — and every other character is a cell with
 * something on it or nothing.
 */
export function parseBoard(lines: readonly string[]): ParsedBoard {
  const rows = rowsOf(lines);
  const ruler = lines.find((line) => line.includes("|")) ?? "";
  const labels = lines[lines.indexOf(ruler) + 1] ?? "";
  const left = firstFile(ruler, labels);

  const out: ParsedBoard = { cells: [], armies: [], heaps: [], belts: [] };
  for (const row of rows) {
    for (let i = 0; i < row.cells.length; i++) {
      const glyph = row.cells[i];
      if (glyph === " ") continue;
      const at = square(left + i, row.rank);
      out.cells.push(at);

      const upper = glyph.toUpperCase();
      if (TYPE_OF[upper] !== undefined) {
        // Upper is side 0 and lower side 1, the usual chess convention.
        const side = (glyph === upper ? 0 : 1) as Color;
        // Facing is not drawn. Zero is north, which `newGame` overrides with
        // the side's own forward for the two classic armies.
        out.armies.push([at, side, TYPE_OF[upper], 0 as Direction]);
        continue;
      }
      if (glyph >= "1" && glyph <= "9") {
        out.heaps.push([at, Number(glyph)]);
        continue;
      }
      if (glyph === "*") {
        out.heaps.push([at, LOTS]);
        continue;
      }
      if (DIR_OF[glyph] !== undefined) {
        // Ownership is not drawn either. Side 0, so a lane at least appears.
        out.belts.push([at, DIR_OF[glyph], 0 as Color]);
        continue;
      }
      if (glyph === "•") {
        // A loaded lane: it is there, and which way it runs is not recoverable.
        out.belts.push([at, 0 as Direction, 0 as Color]);
        continue;
      }
      // `.` is bare ground, `○` is loose material and `▣` a building — all of
      // them cells, and the last two are not reconstructed: a structure takes
      // its square *out* of `cells`, so conjuring one would put a hole in the
      // board where the drawing plainly shows something standing.
    }
  }
  return out;
}

/**
 * A board from a drawing, ready for the renderer.
 *
 * Built by opening a game on it, so the state comes out of the reducer rather
 * than being assembled by hand — `cells`, `armies`, `heaps` and `belts` are
 * all things `new-game` already takes, because a scenario needs every one of
 * them. Never dispatched anywhere: this is a picture of somebody else's game.
 *
 * The empty FEN still lays down the classic sixty-four, which `cells` is then
 * *added* to. Harmless for every board the tuner plays, since both of them
 * contain those squares anyway.
 */
export function boardFrom(lines: readonly string[]): GameState | null {
  const parsed = parseBoard(lines);
  if (parsed.cells.length === 0) return null;
  try {
    return applyCommand(newGame(), {
      type: "new-game",
      fen: "8/8/8/8/8/8/8/8 w - - 0 1",
      cells: parsed.cells,
      armies: parsed.armies,
      heaps: parsed.heaps,
      belts: parsed.belts
    });
  } catch {
    // A drawing this cannot make a board of is a card without a button, never
    // a page that fails to render.
    return null;
  }
}
